import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Users, Loader2, LogIn, UserPlus, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { FloatingLanguageSelector } from '../components/LanguageSelector';
import { stashPendingJoinCode, clearPendingJoinCode, olvidarCodigoUnido, copyDeFalloDeGrupo } from '../lib/groups';
import AvisoAlUnirse from '../components/AvisoAlUnirse';
import { AVISO_PRIVACIDAD_VERSION } from '../lib/avisoPrivacidad';

/* PILOTO-1 (firma #79) — /join/CODIGO, el aterrizaje del link que reparte el
 * profesor.
 *
 * POR QUÉ ESTA RUTA **NO** VA DETRÁS DE `ProtectedRoute`, y es la decisión de
 * diseño de la pantalla: un guard mandaría a /login a quien llega sin sesión y
 * EL CÓDIGO SE PERDERÍA EN EL CAMINO — que es justo el caso más probable, porque
 * el link llega por WhatsApp y lo abre un navegador que no tiene la sesión.
 * Sin guard, la página se pinta siempre y ella decide:
 *
 *   · CON sesión → «Vas a unirte a <grupo> de <profe>» y un botón. Un toque.
 *   · SIN sesión → el código a la vista, guardado en local (lib/groups.js), y
 *     los dos caminos que existen: entrar o crear cuenta. Al volver a Ajustes el
 *     campo ya sale pre-llenado, así que el código no se re-teclea.
 *
 * ESTO NO TOCA NI UNA LÍNEA DEL FLUJO DE AUTENTICACIÓN. Es una consecuencia
 * deliberada: meterle un "regresa aquí después de entrar" a /login sería
 * modificar el camino por el que pasa el 100% de los usuarios para servir a un
 * caso del piloto. El costo es un paso más; el beneficio es cero riesgo sobre la
 * puerta principal.
 *
 * SAFE AREAS: esta pantalla vive FUERA de `AppLayout` (no hay chasis que herede),
 * así que escribe las suyas — arriba y abajo, con `calc` y respaldo `0px`, que en
 * escritorio colapsan a cero y no dejan padding fantasma (precedente firmado:
 * LanguageSelector.jsx).
 */

export default function JoinGroupPage() {
  const { t } = useTranslation(['dashboard', 'common']);
  const { code } = useParams();
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  const [preview, setPreview] = useState(null);   // {name, owner_name}
  // PILOTO-1B suma `expired` a la maquina: un codigo VENCIDO no es un codigo
  // invalido, y el arreglo del alumno es distinto (pedirle el nuevo al profe vs.
  // re-teclear seis caracteres).
  const [state, setState] = useState('idle');     // idle | checking | joining | done | error | expired
  const [alreadyMember, setAlreadyMember] = useState(false);

  const registered = Boolean(user) && !user.is_guest;
  // PILOTO-1C — el GUEST tiene cara propia. El backend ya le contesta 403 y el
  // modal del empujón ya no le pregunta nada; lo que faltaba era la superficie:
  // un invitado que abre el link ve por qué no puede pasar y los dos caminos que
  // sí existen, en vez de un error crudo o de un botón que le va a fallar. El
  // código queda guardado igual que a cualquiera, así que al terminar el
  // registro cae en el mismo empujón que el resto.
  const invitado = Boolean(user) && Boolean(user.is_guest);

  // El código se guarda SIEMPRE al aterrizar, con sesión o sin ella: si el
  // usuario se va a entrar y vuelve por otro camino, Ajustes ya lo tiene.
  useEffect(() => { stashPendingJoinCode(code); }, [code]);

  const check = useCallback(async () => {
    if (!registered) return;
    setState('checking');
    try {
      const res = await api.get(`/groups/lookup/${encodeURIComponent(code)}`);
      setPreview(res.data.group);
      // PILOTO-1C — YA-MIEMBRO ES UN FINAL, NO UN PASO. Antes esto pintaba
      // «Ya estás en este grupo» + un botón que llamaba a `join`, y `join`
      // contestaba 200 con la marca puesta y pintaba una SEGUNDA pantalla casi
      // idéntica: dos superficies, un viaje al servidor y un toque, para
      // decirle al alumno algo que ya sabíamos desde la vista previa.
      //
      // Y EL PENDIENTE SE SUELTA EN ESTE INSTANTE: un miembro no tiene nada
      // pendiente que hacer con ese código, así que no debe volver a verlo en
      // el empujón del siguiente aterrizaje.
      if (res.data.already_member) {
        setAlreadyMember(true);
        // PILOTO-1D — y con el pendiente se va el descarte de ESE codigo: ya es
        // miembro, asi que ni el ofrecimiento ni el recuerdo de haberlo cerrado
        // significan nada.
        olvidarCodigoUnido(code);
        setState('done');
        return;
      }
      setAlreadyMember(false);
      setState('idle');
    } catch (err) {
      // PILOTO-1B — UN VEREDICTO SOBRE EL CODIGO SUELTA EL PENDIENTE. 410 y 404
      // son las dos unicas formas de saber que el codigo esta muerto; un fallo
      // de red o un 5xx no dicen nada de el, asi que ahi el codigo guardado
      // sobrevive y la tarjeta de Ajustes lo sigue ofreciendo pre-llenado.
      const status = err.response?.status;
      if (status === 410) { clearPendingJoinCode(); setState('expired'); return; }
      if (status === 404) { clearPendingJoinCode(); setState('error'); return; }
      // NI VEREDICTO NI MENTIRA. Sin respuesta (red caída, timeout), 5xx o 429 no
      // dicen NADA del código, y decirle «ese código no es válido» a alguien cuyo
      // código está perfecto lo manda a re-teclear seis caracteres buenos. Se
      // vuelve a `idle`: el código sigue a la vista y el botón sigue ahí, así que
      // un toque vuelve a intentarlo — y esa vez es `join` quien contesta la
      // verdad. El pendiente NO se borra.
      setState('idle');
    }
  }, [code, registered]);

  useEffect(() => { if (!loading) check(); }, [loading, check]);

  const join = async () => {
    setState('joining');
    try {
      const res = await api.post('/groups/join', { code, aviso_version: AVISO_PRIVACIDAD_VERSION });
      // post-t7-2c (D4): por si el Panel esta montado en esta pestana, vuelve a pedir el material de los grupos.
      window.dispatchEvent(new CustomEvent('studybuddy:grupos-cambiados'));
      olvidarCodigoUnido(code);
      setPreview(res.data.group);
      setAlreadyMember(Boolean(res.data.already_member));
      setState('done');
    } catch (err) {
      const status = err.response?.status;
      if (status === 410 || status === 404) clearPendingJoinCode();
      if (status === 410) {
        // Se le murio en la mano: el profe roto el codigo entre la vista previa
        // y el boton. Se le dice con el copy que le sirve.
        toast.error(t('groups.join_expired'));
        setState('expired');
        return;
      }
      // PILOTO-1C — el rechazo del SELLO de sesión (403) se pinta en cristiano;
      // el `detail` crudo del servidor queda de red de abajo.
      toast.error(copyDeFalloDeGrupo(err, t, 'dashboard:groups.toasts.join_failed'));
      setState('error');
    }
  };

  return (
    <div
      className="min-h-dvh flex items-center justify-center px-5 bg-[#0e0e0e] pt-[calc(1.5rem+env(safe-area-inset-top,0px))] pb-[calc(1.5rem+env(safe-area-inset-bottom,0px))]"
      data-testid="join-page">
      <FloatingLanguageSelector />
      <div className="w-full max-w-md space-y-6">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#FF6B00]/10 mb-4">
            <Users className="w-8 h-8 text-[#FF6B00]" />
          </div>
          <h1 className="text-2xl font-bold text-white" data-testid="join-title">{t('groups.join_page_title')}</h1>
        </div>

        <div className="glass-card rounded-[20px] p-6 space-y-5">
          {/* El código, siempre a la vista: es lo que hay que poder leer y
              re-teclear si algo del camino automático falla. */}
          <div className="text-center">
            <p className="text-[10px] uppercase tracking-wider text-[#555] mb-1">{t('groups.code_label')}</p>
            <p className="font-mono text-2xl text-[#FF6B00] tracking-[0.25em] break-all" data-testid="join-code">
              {code}
            </p>
          </div>

          {loading ? (
            // post-t7-2c (F, Anexo D.3): la web ya no tapa el arranque con el logo, asi que
            // esta espera queda a la vista: conserva su alto y no gira (cero spinner).
            <div className="flex justify-center py-2" data-testid="join-loading" aria-busy="true">
              <span className="w-5 h-5" />
            </div>
          ) : !registered ? (
            /* SIN SESIÓN (o con una de INVITADO): los dos caminos que existen, y
               el aviso de que el código quedó guardado.
               PILOTO-1C — el invitado comparte esta superficie porque comparte la
               salida (crear cuenta o entrar); lo único que cambia es la frase de
               arriba, que en su caso tiene que decir POR QUÉ no basta con lo que
               ya tiene. Un guest no puede pertenecer a un grupo: su correo es
               desechable y el grupo moriría con su sesión (services/groups.py). */
            <div className="space-y-3" data-testid={invitado ? 'join-guest' : 'join-logged-out'}>
              <p className="text-sm text-[#a0a0a0] text-center" data-testid="join-need-account">
                {invitado ? t('groups.join_guest_needs_account') : t('groups.join_need_account')}
              </p>
              <Button onClick={() => navigate('/login')}
                className="w-full bg-[#FF6B00] hover:bg-[#E05A00] text-white rounded-xl min-h-[44px] text-sm"
                data-testid="join-login-btn">
                <LogIn className="w-4 h-4 mr-2" /> {t('common:nav.log_in')}
              </Button>
              <Button variant="outline" onClick={() => navigate('/signup')}
                className="w-full border-[#333] text-[#a0a0a0] hover:text-white hover:border-[#555] rounded-xl min-h-[44px] text-sm"
                data-testid="join-signup-btn">
                <UserPlus className="w-4 h-4 mr-2" /> {t('common:nav.create_account')}
              </Button>
              <p className="text-xs text-[#555] text-center">{t('groups.join_code_saved')}</p>
            </div>
          ) : state === 'done' ? (
            /* LA ÚNICA SUPERFICIE DEL FINAL, para las dos maneras de llegar a él
               (acabo de entrar / ya estaba dentro): el código arriba, una línea
               que dice qué pasó, y «Continuar» al panel. PILOTO-1C la volvió
               también la del ya-miembro, que antes veía ésta DESPUÉS de otra
               casi igual. */
            <div className="space-y-4 text-center" data-testid="join-done">
              <p className="text-sm text-white line-clamp-2" data-testid="join-done-msg">
                {alreadyMember
                  ? t('groups.toasts.already_member', { name: preview?.name })
                  : t('groups.toasts.joined', { name: preview?.name })}
              </p>
              <Button onClick={() => navigate('/dashboard')}
                className="w-full bg-[#FF6B00] hover:bg-[#E05A00] text-white rounded-xl min-h-[44px] text-sm"
                data-testid="join-continue-btn">
                {t('groups.join_continue_btn')} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          ) : (state === 'error' || state === 'expired') ? (
            <div className="space-y-4 text-center"
              data-testid={state === 'expired' ? 'join-expired' : 'join-error'}>
              <p className="text-sm text-[#a0a0a0]">
                {state === 'expired' ? t('groups.join_expired') : t('groups.join_invalid')}
              </p>
              <Button variant="outline" onClick={() => navigate('/settings')}
                className="w-full border-[#333] text-[#a0a0a0] hover:text-white hover:border-[#555] rounded-xl min-h-[44px] text-sm"
                data-testid="join-settings-btn">
                {t('groups.join_go_settings')}
              </Button>
            </div>
          ) : (
            <div className="space-y-4" data-testid="join-ready">
              {/* PILOTO-1C — AQUÍ YA NO SE PINTA EL «ya estás en este grupo».
                  Ese caso no llega hasta acá: `check` lo cierra en la pantalla
                  compacta de arriba (`join-done`) en cuanto la vista previa lo
                  dice. Esta rama es sólo la del alumno que TODAVÍA no entra. */}
              {preview && (
                <div className="text-center">
                  {/* Elipsis: el tope de nombre son 60 caracteres y este bloque
                      vive sobre una tarjeta de ancho fijo. */}
                  <p className="text-base font-semibold text-white truncate" data-testid="join-group-name">{preview.name}</p>
                  {preview.owner_name && (
                    <p className="text-xs text-[#6a6a6a] mt-1 truncate">
                      {t('groups.taught_by', { name: preview.owner_name })}
                    </p>
                  )}
                </div>
              )}
              {/* post-t7-2b (A1, #613): lo que vera el profe, debajo del nombre del grupo y antes del boton. */}
              <AvisoAlUnirse className="text-center" />
              <Button onClick={join} disabled={state === 'joining' || state === 'checking'}
                className="w-full bg-[#FF6B00] hover:bg-[#E05A00] text-white rounded-xl min-h-[44px] text-sm disabled:opacity-40"
                data-testid="join-confirm-btn">
                {state === 'joining' || state === 'checking'
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : t('groups.join_btn')}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
